import { useEffect, useState, useCallback } from "react";
import axiosClient from "@service/axiosClient";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent, ITextFilterParams } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch } from "react-icons/fi";

import { IPlantDTO } from "@interfaces/IPlantDTO";
import { Box } from "@mui/material";
import Input from "@components/Input";
import { ISelect } from "@interfaces/ISelect";
import { fetchEngagementStatus } from "@service/PlantService";
import { IEngagementStatusesDTO } from "@interfaces/IEngagementStatusesDTO";
import StatusDisplay from "../../components/payroll_components/StatusDisplay";
import { SelectFilter } from "@components/SelectFilter";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";

export function DashBoardLanding(): JSX.Element {
  const [plants, setPlants] = useState<IPlantDTO[]>([]);
  const [engagementStatuses, setEngagementStatuses] = useState<ISelect[]>([]);
  const [selectedEngagementStatus, setSelectedEngagementStatus] =
    useState<ISelect>({ id: 0, value: "active_billable" });

  const [isLoading, setIsLoading] = useState(true);

  const breakpoint = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint, isMobile]);

  useEffect(() => {
    async function fetchPlants() {
      setIsLoading(false);
      try {
        const response = await axiosClient.get(
          `/plant_api/plant/upload_status/${selectedEngagementStatus.value}`
        );
        setPlants(response.data);
        const engagementStatuses = await fetchEngagementStatus();
        const engagementStatusOptions = [
          { id: 0, value: "all" },
          ...(engagementStatuses ?? []).map(
            (engagement: IEngagementStatusesDTO) => ({
              id: engagement.id,
              value: engagement.name,
            })
          ),
        ] as ISelect[];

        setEngagementStatuses(engagementStatusOptions);
      } catch (error) {
        console.error("Error fetching plants:", error);
      }
    }
    if (isLoading) {
      fetchPlants();
    }
  }, [isLoading, selectedEngagementStatus]);

  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
  };
  const [searchVal, setsearchVal] = useState("");

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      gridApi.setQuickFilter(val);
      setsearchVal(val);
    }
  };

  const columnDefs = [
    {
      headerName: "Plant Name",
      field: "plant_name",
      // flex: 1,
      width: Math.round(viewportWidth * 0.3),
      filter: true,
    },
    {
      headerName: "Status",
      field: "status",
      width: Math.round(viewportWidth * 0.2),
      flex: 1,
      filter: true,
      headerClass: "header-center",
      filterParams: {
        defaultOption: true,
      } as unknown as ITextFilterParams,
      cellRenderer: (params: any): JSX.Element => {
        return <StatusDisplay status={params.value} className="tw-min-w-20" />;
      },
    },
    // {
    //   headerName: "Engagement Status",
    //   field: "engagementStatus",
    //   width: 155,
    //   filter: true,
    //   filterParams: {
    //     defaultOption: true,
    //   } as unknown as ITextFilterParams,
    //   cellRenderer: (params: any): JSX.Element => {
    //     return <StatusDisplay status={params.value} />;
    //   },
    // },
  ];

  function handleChangeEngagementStatus(selected: ISelect) {
    setSelectedEngagementStatus(selected);
    setIsLoading(true);
  }

  return (
    <div className="tw-flex tw-justify-center">
      <div
        className={`tw-flex tw-flex-col tw-h-full ${
          isMobile ? "tw-w-screen tw-px-2" : "tw-w-[55vw]"
        }`}
      >
        <Box
          sx={{
            padding: "1em",
          }}
        >
          <div className="tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md tw-h-[85vh] tw-overflow-y-auto">
            <h1 className="tw-mb-6 tw-color-primary">Plant Upload Statuses</h1>

            {!isLoading && (
              <div className="tw-mb-4 tw-m-auto">
                <div className="tw-flex tw-mb-4 tw-h-[3.5vh]">
                  <Input
                    icon={FiSearch}
                    containerStyle={{ marginRight: 4, height: 32 }}
                    id="search"
                    type="text"
                    name="search"
                    placeholder="search"
                    onChange={(e: any) => updateSearchQuery(e.target.value)}
                    disabled={false}
                    value={searchVal}
                  />
                  <SelectFilter
                    className="tw-max-w-44"
                    select={selectedEngagementStatus}
                    options={engagementStatuses}
                    onSelect={handleChangeEngagementStatus}
                    placeholder="Select..."
                    disabled={false}
                  />
                </div>
                {/* {!isMobile ? ( */}
                <div onContextMenu={handleContextMenu}>
                  <AgGridReact
                    className="ag-theme-balham"
                    domLayout="autoHeight"
                    columnDefs={columnDefs}
                    rowHeight={50}
                    rowData={plants}
                    gridOptions={{}}
                    rowSelection="multiple"
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    onCellContextMenu={handleCellContextMenu}
                    colResizeDefault="shift"
                    animateRows
                    pagination
                    pivotPanelShow="always"
                    rowStyle={{ fontSize: "14px" }}
                  />
                  {ContextMenuComponent}
                </div>
              </div>
            )}
          </div>
        </Box>
      </div>
    </div>
  );
}
